"use client";

import { Minus, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Dictionary } from "@/types/i18n";

type CvUploadFields = Dictionary["cvUploadForm"]["fields"];

/** Shared by every free-text control in the mobile form. */
export const MOBILE_FIELD_CLASS =
  "min-h-11 rounded-[8px] border-border-input bg-background px-[14px] text-base font-medium leading-6 text-text-muted shadow-button placeholder:text-text-placeholder placeholder:font-normal focus-visible:border-border-input focus-visible:ring-4 focus-visible:ring-surface-tertiary";

const LABEL_CLASS = "text-sm font-medium leading-5 text-text-heading";

interface CvTargetingSectionProps {
  fields: CvUploadFields;
  isOpen: boolean;
  onToggle: () => void;
  showProgramCampus: boolean;
  targetJobTitle: string;
  onTargetJobTitleChange: (value: string) => void;
  programCampus: string;
  onProgramCampusChange: (value: string) => void;
  jobDescription: string;
  onJobDescriptionChange: (value: string) => void;
}

export function CvTargetingSection({
  fields,
  isOpen,
  onToggle,
  showProgramCampus,
  targetJobTitle,
  onTargetJobTitleChange,
  programCampus,
  onProgramCampusChange,
  jobDescription,
  onJobDescriptionChange,
}: CvTargetingSectionProps) {
  const toggleCopy = fields.targetingToggle;

  return (
    <>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls="cv-targeting-fields"
        onClick={onToggle}
        className="flex min-h-11 w-full items-center justify-between rounded-[8px] border border-border-input bg-background px-[14px] text-left text-sm font-medium leading-5 text-text-heading shadow-button"
      >
        <span>{isOpen ? toggleCopy.hide : toggleCopy.show}</span>
        {isOpen ? (
          <Minus className="size-4" aria-hidden="true" />
        ) : (
          <Plus className="size-4" aria-hidden="true" />
        )}
      </button>

      {isOpen ? (
        <div
          id="cv-targeting-fields"
          className="space-y-4 rounded-[8px] border border-border-primary bg-background/60 p-3"
        >
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mobile-target-job-title" className={LABEL_CLASS}>
                {fields.targetJobTitle.label}
              </Label>
              <Input
                id="mobile-target-job-title"
                value={targetJobTitle}
                onChange={(event) => onTargetJobTitleChange(event.target.value)}
                placeholder={fields.targetJobTitle.placeholder}
                className={MOBILE_FIELD_CLASS}
              />
            </div>
            {showProgramCampus ? (
              <div className="space-y-2">
                <Label htmlFor="mobile-program-campus" className={LABEL_CLASS}>
                  {fields.programCampus.label}
                </Label>
                <Input
                  id="mobile-program-campus"
                  value={programCampus}
                  onChange={(event) =>
                    onProgramCampusChange(event.target.value)
                  }
                  placeholder={fields.programCampus.placeholder}
                  className={MOBILE_FIELD_CLASS}
                />
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile-job-description" className={LABEL_CLASS}>
              {fields.jobDescription.label}
            </Label>
            <Textarea
              id="mobile-job-description"
              value={jobDescription}
              onChange={(event) => onJobDescriptionChange(event.target.value)}
              placeholder={fields.jobDescription.placeholder}
              rows={2}
              className={`${MOBILE_FIELD_CLASS} py-[10px]`}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
